import React, { forwardRef, useMemo } from 'react';
import PDFPage from '../PDFPage';
import styles from '../PDFReportPreview.module.css';
import { RevenueType, revenueTypeOptions } from 'components/RevenueWidgets/helpers';
import { monthForSelect } from 'utils/monthYear';
import { useRevenueContext } from 'contexts/RevenueContext';
import AGTAbleWrapper from 'components/AGTable/AGTableWrapper';
import { getHistoricalTableData } from './utils';
import { getLabelFromOptions } from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';

type HistoricalPageProps = {
    spotAddress: string;
    dateTime: string;
    chartCanvas: HTMLCanvasElement | null;
    filters: { yoyRevenueType: RevenueType; month: string };
};

const HistoricalPage = forwardRef<HTMLDivElement, HistoricalPageProps>(function HistoricalPage(
    { spotAddress, dateTime, chartCanvas, filters },
    ref
) {
    const { yoyRevenueReport } = useRevenueContext();

    const tableData = useMemo(
        () =>
            getHistoricalTableData({
                yoyRevenueReport,
                yoyRevenueType: filters.yoyRevenueType,
            }),
        [yoyRevenueReport, filters.yoyRevenueType]
    );

    const filtersInfo = useMemo(() => {
        return [
            { label: 'Metric', value: getLabelFromOptions(revenueTypeOptions, filters.yoyRevenueType) },
            { label: 'Month', value: getLabelFromOptions(monthForSelect, filters.month) },
        ];
    }, [filters]);

    return (
        <PDFPage
            ref={ref}
            spotAddress={spotAddress}
            dateTime={dateTime}
            title="Historical Comparisons"
            filters={filtersInfo}
        >
            {chartCanvas ? (
                <div className={styles.contentContainer}>
                    <img
                        src={chartCanvas.toDataURL('image/png')}
                        alt="Historical Comparisons"
                        className={styles.capturedChart}
                    />
                    <AGTAbleWrapper className={styles.firstColumnColored}>
                        <thead>
                            <tr>
                                {tableData.headers.map((header) => (
                                    <th key={header}>{header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.rows.map((row, index) => (
                                <tr key={index}>
                                    {row.map((cell, cellIndex) => (
                                        <td key={cellIndex}>{cell}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </AGTAbleWrapper>
                </div>
            ) : (
                <div className={styles.noDataMessage}>
                    <p>No data available for this chart</p>
                </div>
            )}
        </PDFPage>
    );
});

export default HistoricalPage;
